import { showErrorToast } from "@/shared/toast"
import MenuItem from "@mui/material/MenuItem"
import Button from "@mui/material/Button"
import { useMutation } from "@apollo/client/react"
import { useUpdateReservationValidationSchema } from "@/forms/update_reservation"
import { UpdateReservationDocument } from "@/graphql/graphql"
import Alert from "@mui/material/Alert"
import { Formik } from "formik"
import { formatTableName } from "@/shared/tables"
import LoadingButton from "@mui/lab/LoadingButton"
import { Divider, InputAdornment } from "@mui/material"
import Avatar from "@mui/material/Avatar"
import ClickAwayListener from "@mui/material/ClickAwayListener"
import Paper from "@mui/material/Paper"
import Popper from "@mui/material/Popper"
import { Check, Clock, MapPin } from "lucide-mui"
import { DateTime } from "luxon"
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { TextField } from "@/components/form"

import CustomerBox from "@/components/reservations/shared/customer_box"
import RemoveButton from "@/components/reservations/shared/remove_button"
import StatusSelector from "@/components/reservations/shared/status_selector"
import { RESERVATION_COLORS, ReservationStatus } from "@/constants/reservations"
import { UPDATE_RESERVATION_REFETCH_QUERIES } from "@/graphql/dependencies"
import i18n from "@/i18n"
import { createHoursList } from "@/shared/datetime"
import { usePanelLink } from "@/shared/hooks"
import { useStore } from "@/store"

import PeopleAmountInput from "../../people_amount_input"

const normalizePeopleAmount = (value: unknown): number | null => {
  if (value == null) return null
  if (typeof value === "string" && value.trim() === "") return null
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return null
  return n
}

const STYLES = {
  paper: "overflow-hidden w-96 rounded-lg",
  header: "px-2 py-1 text-white text-sm font-semibold",
  content: "px-4 pt-4",
  alert: "m-1",
  fieldContainer: "mb-3",
  divider: "opacity-30 mb-4",
  dividerSmall: "opacity-30 mb-3",
  noteContainer: "px-6 py-1 mb-4",
  note: "bg-gray-100 text-gray-700 text-sm py-2 px-2 rounded",
  noteAuthor: "flex flex-row items-center text-xs mt-2",
  noteGuestLabel: "text-xs text-gray-400 ml-1",
  statusContainer: "px-6 py-1 mb-4",
  footer: "flex items-center px-6 mb-3",
  footerRight: "ml-auto flex items-center gap-2",
}

// TODO: #STARRTIMES
const startHour = 8
const endHour = 4

const getHeaderBackgroundColor = (status: ReservationStatus) => {
  return RESERVATION_COLORS[status]?.main || RESERVATION_COLORS.removed.main
}

const Preview = ({ venue, reservation, target, onClose }) => {
  const [updateReservation, { loading }] = useMutation(UpdateReservationDocument, {
    refetchQueries: UPDATE_RESERVATION_REFETCH_QUERIES,
  })

  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const validationSchema = useUpdateReservationValidationSchema({ venue })
  const { currentDate } = useStore()

  const linkTo = usePanelLink()

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => setSuccess(null), 2000)
      return () => clearTimeout(timeout)
    }
  }, [success])

  // TODO: #NETWORKEXCEPTIONS
  const onSubmit = async (values) => {
    setError(null)

    const peopleAmountValue = normalizePeopleAmount(values.peopleAmount)

    const result = await updateReservation({
      variables: {
        reservationId: reservation.id,
        startTime: values.startTime,
        endTime: values.endTime,
        peopleAmount: peopleAmountValue,
        tableId: values.tableId,
        status: values.status,
      },
    })

    const success = result?.data?.updateReservation?.success
    // const errors = result?.data?.updateReservation?.errors || []

    if (success) {
      setSuccess(true)
    } else {
      setError(i18n.t("app.reservation_preview.update_error"))
      showErrorToast(i18n.t("app.reservation_preview.update_error"))
      console.error("Error creating reservation", result)
      return
    }
  }

  const adornments: Record<string, React.ReactElement> = Object.entries({ MapPin, Clock }).reduce(
    (acc, [name, Icon]) => {
      acc[name] = (
        <InputAdornment position="start">
          <Icon className="text-lg" />
        </InputAdornment>
      )
      return acc
    },
    {},
  )

  return (
    <Popper
      open={!!reservation}
      anchorEl={target}
      placement="right-start"
      sx={{ zIndex: 100 }}
      data-testid="panel-reservations-calendar-preview"
    >
      {!!reservation && (
        <ClickAwayListener onClickAway={onClose} mouseEvent="onMouseUp">
          <div>
            <Formik
              initialValues={{
                tableId: reservation.table.id,
                peopleAmount: reservation.peopleAmount,
                startTime: DateTime.fromISO(reservation.startTime),
                endTime: DateTime.fromISO(reservation.endTime),
                status: reservation.status,
              }}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {(formik) => {
                const currentStatus = formik.values.status as ReservationStatus
                const headerBgColor = getHeaderBackgroundColor(currentStatus)

                return (
                  <form onSubmit={formik.handleSubmit}>
                    <Paper className={STYLES.paper}>
                      <div className={STYLES.header} style={{ backgroundColor: headerBgColor }}>
                        #{reservation.id}
                      </div>

                      {!!reservation.customerContact && (
                        <CustomerBox
                          customerId={reservation.customer?.id}
                          customerContact={reservation.customerContact}
                          className="m-4 mb-6"
                        />
                      )}

                      <div className={STYLES.content}>
                        {!!error && (
                          <Alert className={STYLES.alert} severity="error">
                            {error}
                          </Alert>
                        )}

                        <div className={STYLES.fieldContainer}>
                          <TextField name="tableId" label="Table" select startAdornment={adornments.MapPin} fullWidth>
                            {venue.tables
                              .filter((table) => !table.deleted)
                              .map((table) => (
                                <MenuItem key={table.id} value={table.id}>
                                  {formatTableName(table)}
                                </MenuItem>
                              ))}
                          </TextField>
                        </div>

                        <div className={STYLES.fieldContainer}>
                          <PeopleAmountInput name="peopleAmount" label="Seats" />
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <TextField
                            name="startTime"
                            label={i18n.t("app.reservation.labels.time")}
                            select
                            startAdornment={adornments.Clock}
                          >
                            {createHoursList(currentDate, startHour, endHour).map(([time, date]) => (
                              <MenuItem key={time} value={date.toISO()}>
                                {time}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField name="endTime" label="" select startAdornment={adornments.Clock}>
                            {createHoursList(currentDate, startHour, endHour).map(([time, date]) => (
                              <MenuItem key={time} value={date.toISO()}>
                                {time}
                              </MenuItem>
                            ))}
                          </TextField>
                        </div>
                      </div>

                      <Divider className={STYLES.divider} />

                      {!!reservation.customerNote && !!reservation.customerContact && (
                        <>
                          <div className={STYLES.noteContainer}>
                            <div className={STYLES.note}>
                              <div>{reservation.customerNote}</div>
                              <div className={STYLES.noteAuthor}>
                                <Avatar src={reservation.customerContact.avatarUrl} className="w-4 h-4 mr-1" />
                                {reservation.customerContact.name}
                                <span className={STYLES.noteGuestLabel}> (Guest)</span>
                              </div>
                            </div>
                          </div>
                          <Divider className={STYLES.divider} />
                        </>
                      )}

                      <div className={STYLES.statusContainer}>
                        <StatusSelector />
                      </div>

                      <Divider className={STYLES.dividerSmall} />

                      <div className={STYLES.footer}>
                        <RemoveButton reservation={reservation} setError={setError} onSuccess={onClose} />

                        <div className={STYLES.footerRight}>
                          <Button
                            color="info"
                            component={Link}
                            to={linkTo(`reservations/${reservation.id}/edit`)}
                            data-testid="panel-reservations-calendar-preview-more-btn"
                          >
                            {i18n.t("app.reservation_preview.buttons.more")}
                          </Button>

                          <LoadingButton
                            color={success ? "success" : "primary"}
                            loadingPosition="center"
                            loading={loading}
                            type="submit"
                            data-testid="panel-reservations-calendar-preview-save-btn"
                          >
                            {success ? <Check /> : "Save"}
                          </LoadingButton>
                        </div>
                      </div>
                    </Paper>
                  </form>
                )
              }}
            </Formik>
          </div>
        </ClickAwayListener>
      )}
    </Popper>
  )
}

export default Preview
